import { srcSetFor, webpSrcSetFor } from "@/lib/images";

interface PhotoProps {
  /** Path to the base JPEG, e.g. "/assets/images/hero-renovation.jpg". */
  src: string;
  /** Widths generated on disk for this image — see `@/lib/images`. */
  widths: number[];
  /** The `sizes` attribute; use one of the SIZES_* constants. */
  sizes: string;
  alt: string;
  className?: string;
  /** Intrinsic dimensions. Always pass these — they are what stops the image from shifting layout. */
  width: number;
  height: number;
  loading?: "eager" | "lazy";
  /** Set "high" on the one image that is the page's LCP element. */
  priority?: boolean;
}

/**
 * A responsive `<picture>` for the photographs in /public/assets/images.
 *
 * Offers the WebP derivatives first and falls back to the original JPEG, so
 * browsers that cannot decode WebP still get a picture. The wrapper is
 * `display: contents`, which keeps it out of layout entirely — the `<img>`
 * positions against whatever ancestor it did before, so existing
 * `absolute inset-0` styling behaves exactly as it always has.
 */
const Photo = ({
  src,
  widths,
  sizes,
  alt,
  className,
  width,
  height,
  loading = "lazy",
  priority = false,
}: PhotoProps) => (
  <picture className="contents">
    <source type="image/webp" srcSet={webpSrcSetFor(src, widths)} sizes={sizes} />
    <img
      src={src}
      srcSet={srcSetFor(src, widths)}
      sizes={sizes}
      alt={alt}
      className={className}
      width={width}
      height={height}
      loading={loading}
      decoding={priority ? "sync" : "async"}
      // React 18 only forwards the all-lowercase spelling of this attribute.
      {...(priority ? { fetchpriority: "high" } : {})}
    />
  </picture>
);

export default Photo;
